import React, { useState, useEffect } from "react";
import baseUrl from "../../api/baseUrl";
import logo from "../../assets/icons/Care32-01.png";
import getTokenEncript from "../../api/encriptToken";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import LoginIcon from "@mui/icons-material/Login";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BadgeIcon from "@mui/icons-material/Badge";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

interface Clinic {
  clinicId: string;
  clinicName: string;
  employeeId: string;
  roleName: string;
}

function Drlogin() {
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [formdata, setformdata] = useState({
    email: "",
    password: "",
    error: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showClinicSelection, setShowClinicSelection] = useState(false);
  const [availableClinics, setAvailableClinics] = useState<Clinic[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") as "light" | "dark";
    const htmlTheme = document.documentElement.getAttribute("data-theme") as
      | "light"
      | "dark";

    if (savedTheme) {
      setTheme(savedTheme);
    } else if (htmlTheme) {
      setTheme(htmlTheme);
    }

    // Listen for theme changes
    const observer = new MutationObserver(() => {
      const currentTheme = document.documentElement.getAttribute(
        "data-theme"
      ) as "light" | "dark";
      if (currentTheme) {
        setTheme(currentTheme);
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  const handlechange = (e: any) => {
    setformdata({ ...formdata, [e.target.name]: e.target.value, error: false });
  };

  const fetchImageAsBlob = async (imageUrl: string) => {
    const url = String(imageUrl ?? "").trim();
    if (!url || url.startsWith("blob:")) return "";
    // Prefer durable http(s)/data URLs for certificates (blob URLs die on refresh)
    if (/^https?:\/\//i.test(url) || url.startsWith("data:image/")) return url;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch {
      return "";
    }
  };

  const onSubmit = (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    localStorage.clear();
    baseUrl
      .post("/employee/employee-login", formdata)
      .then(async (response) => {
        // Check if multiple clinics are available
        if (response.data.drop && response.data.drop.length > 0) {
          // Show clinic selection on the same page
          setAvailableClinics(response.data.drop);
          setShowClinicSelection(true);
          setIsLoading(false);
        } else {
          // Single clinic - proceed with normal login
          console.log(response.data);
          
          const hashData: Array<{ key: string; value: string }> = [
            { key: "token", value: response.data.token },
            { key: "user", value: response.data.user },
            { key: "eid", value: response.data.eid },
            { key: "cid", value: response.data.clinicId },
            { key: "userName", value: response.data.userName },
            { key: "routePermission", value: response.data.routePermission.join(", ") },
            { key: "accessPermission", value: response.data.actionPermission.join(", ") },
            { key: "clinicName", value: response.data.clinicName },
            { key: "clinicPhoneNumber", value: response.data.clinicPhoneNumber },
            { key: "printImage", value: await fetchImageAsBlob(response.data.printImage) },
            { key: "title", value: response.data.title },
            { key: "clinicLogo", value: response.data.clinicLogo },
            { key: "sms", value: response.data.sms ? "true" : "false" },
            { key: "role", value: response.data.roleName },
            { key: "address", value: response.data.address },
            { key: "advancedChart", value: response.data.advancedChart ? "true" : "false" },
            {
              key: "featureLabs",
              value: response.data.featureList?.labs ? "true" : "false",
            },
            {
              key: "featurePromotionalSms",
              value: response.data.featureList?.promotionalSms ? "true" : "false",
            },
            {
              key: "featureRoomAllocation",
              value: response.data.featureList?.roomAllocation ? "true" : "false",
            },
            {
              key: "featurePrintTemplate2",
              value: response.data.featureList?.printTemplate_2 ? "true" : "false",
            },
            {
              key: "featureDoctorTransfer",
              value: response.data.featureList?.doctorTransfer ? "true" : "false",
            },
            {
              key: "featureCertificatePrint",
              value: response.data.featureList?.certificatePrint ? "true" : "false",
            },
            {
              key: "featureInventory",
              value: response.data.featureList?.inventory ? "true" : "false",
            },
            {
              key: "featureBookMyDentistOnly",
              value: response.data.featureList?.bookMyDentistOnly ? "true" : "false",
            },
            { key: "timeDuration", value: response.data.timeDuration || "10" },
          ];

          getTokenEncript(hashData);
          window.location.reload();
          window.location.href = "/client/dashboard";
        }
      })
      .catch((error) => {
        setformdata({ ...formdata, error: true });
        setIsLoading(false);
        console.error(error);
      });
  };

  const handleClinicProceed = async () => {
    if (!selectedEmployeeId) return;

    setIsLoading(true);
    try {
      // Find the selected clinic data
      const selectedClinic = availableClinics.find(
        (clinic) => clinic.employeeId === selectedEmployeeId
      );

      if (!selectedClinic) {
        throw new Error("Selected clinic not found");
      }

      // Store selected clinic in localStorage
      localStorage.setItem("selectedClinic", selectedClinic.clinicId);
      localStorage.setItem("selectedClinicName", selectedClinic.clinicName || "");

      // Login with the specific employee using employeeId
      const loginResponse = await baseUrl.post("/employee/dr-login", {
        employeeId: selectedEmployeeId,
        password: formdata.password,
      });

      const hashData: Array<{ key: string; value: string }> = [
        { key: "token", value: loginResponse.data.token },
        { key: "user", value: loginResponse.data.user },
        { key: "eid", value: loginResponse.data.eid },
        { key: "cid", value: loginResponse.data.clinicId },
        { key: "userName", value: loginResponse.data.userName },
        {
          key: "routePermission",
          value: loginResponse.data.routePermission.join(", "),
        },
        {
          key: "accessPermission",
          value: loginResponse.data.actionPermission.join(", "),
        },
        { key: "clinicName", value: loginResponse.data.clinicName },
        {
          key: "clinicPhoneNumber",
          value: loginResponse.data.clinicPhoneNumber,
        },
        {
          key: "printImage",
          value: await fetchImageAsBlob(loginResponse.data.printImage),
        },
        { key: "title", value: loginResponse.data.title },
        { key: "clinicLogo", value: loginResponse.data.clinicLogo },
        { key: "sms", value: loginResponse.data.sms ? "true" : "false" },
        { key: "role", value: loginResponse.data.roleName },
        { key: "address", value: loginResponse.data.address },
        { key: "advancedChart", value: loginResponse.data.advancedChart ? "true" : "false" },
        {
          key: "featureLabs",
          value: loginResponse.data.featureList?.labs ? "true" : "false",
        },
        {
          key: "featurePromotionalSms",
          value: loginResponse.data.featureList?.promotionalSms ? "true" : "false",
        },
        {
          key: "featureRoomAllocation",
          value: loginResponse.data.featureList?.roomAllocation ? "true" : "false",
        },
        {
          key: "featurePrintTemplate2",
          value: loginResponse.data.featureList?.printTemplate_2 ? "true" : "false",
        },
        {
          key: "featureDoctorTransfer",
          value: loginResponse.data.featureList?.doctorTransfer ? "true" : "false",
        },
        {
          key: "featureCertificatePrint",
          value: loginResponse.data.featureList?.certificatePrint ? "true" : "false",
        },
        {
          key: "featureInventory",
          value: loginResponse.data.featureList?.inventory ? "true" : "false",
        },
        {
          key: "featureBookMyDentistOnly",
          value: loginResponse.data.featureList?.bookMyDentistOnly ? "true" : "false",
        },
        { key: "timeDuration", value: loginResponse.data.timeDuration || "10" },
      ];

      getTokenEncript(hashData);
      
      // Navigate after successful completion
      window.location.reload();
      window.location.href = "/client/dashboard";
    } catch (error: any) {
      console.error("Error proceeding with clinic selection:", error);
      setIsLoading(false);
      setformdata({ ...formdata, error: true });
      alert(
        error.response?.data?.message ||
          "Failed to proceed. Please try again."
      );
    }
  };

  const isDark = theme === "dark";

  return (
    <div
      className={`min-h-screen flex items-center justify-center relative overflow-hidden ${
        isDark
          ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
          : "bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30"
      }`}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className={`absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl ${
            isDark ? "bg-blue-500/10" : "bg-blue-500/20"
          }`}
        ></div>
        <div
          className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl ${
            isDark ? "bg-indigo-500/10" : "bg-indigo-500/20"
          }`}
        ></div>
        <div
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl ${
            isDark ? "bg-purple-500/5" : "bg-purple-500/10"
          }`}
        ></div>
      </div>

      {/* Floating Tooth Icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className={`absolute text-6xl animate-pulse ${
              isDark ? "text-white/5" : "text-slate-300/20"
            }`}
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.5}s`,
            }}
          >
            🦷
          </div>
        ))}
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div
          className={`backdrop-blur-xl rounded-3xl shadow-2xl border overflow-hidden ${
            isDark
              ? "bg-slate-800/90 border-slate-700"
              : "bg-white/90 border-slate-200"
          }`}
        >
          {/* Logo Section - Only show when not in clinic selection */}
          {!showClinicSelection && (
            <div className="pt-6 pb-4 px-8 text-center">
              <div
                className={`rounded-2xl shadow-lg mb-4 py-3 px-6 inline-block h-16 flex items-center justify-center ${
                  isDark ? "bg-slate-700" : "bg-white"
                }`}
              >
                <img src={logo} alt="Care32" className="h-auto w-auto object-contain" />
              </div>
              <h1
                className={`text-3xl font-bold mb-2 ${
                  isDark ? "text-white" : "text-slate-800"
                }`}
              >
                Welcome Back
              </h1>
              <p
                className={`text-sm ${
                  isDark ? "text-slate-400" : "text-slate-600"
                }`}
              >
                Sign in to your dental practice account
              </p>
            </div>
          )}
          
          {/* Logo Section - Minimal for clinic selection */}
          {showClinicSelection && (
            <div className="pt-6 pb-4 px-8 text-center">
              <div
                className={`rounded-2xl shadow-lg mb-4 py-3 px-6 inline-block h-16 flex items-center justify-center ${
                  isDark ? "bg-slate-700" : "bg-white"
                }`}
              >
                <img src={logo} alt="Care32" className="h-auto w-auto object-contain" />
              </div>
            </div>
          )}

          {/* Form Section or Clinic Selection */}
          <div className="px-8 pb-10">
            {showClinicSelection ? (
              // Clinic Selection UI
              <>
                <div className="mb-5">
                  <div className="text-center">
                    <h2
                      className={`text-xl font-bold mb-1.5 ${
                        isDark ? "text-white" : "text-slate-800"
                      }`}
                    >
                      Select Your Clinic
                    </h2>
                    <p
                      className={`text-xs ${
                        isDark ? "text-slate-400" : "text-slate-600"
                      }`}
                    >
                      You have access to {availableClinics.length} clinic{availableClinics.length > 1 ? "s" : ""}. Choose one to continue.
                    </p>
                  </div>
                </div>

                <div 
                  className="clinic-list-container space-y-2.5 mb-5 max-h-[350px] overflow-y-auto pr-2"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: isDark 
                      ? 'rgba(255,255,255,0.2) rgba(255,255,255,0.05)' 
                      : 'rgba(0,0,0,0.2) rgba(0,0,0,0.05)',
                  }}
                >
                  <style>{`
                    .clinic-list-container::-webkit-scrollbar {
                      width: 6px;
                    }
                    .clinic-list-container::-webkit-scrollbar-track {
                      background: ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"};
                      border-radius: 10px;
                    }
                    .clinic-list-container::-webkit-scrollbar-thumb {
                      background: ${isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"};
                      border-radius: 10px;
                    }
                    .clinic-list-container::-webkit-scrollbar-thumb:hover {
                      background: ${isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)"};
                    }
                  `}</style>
                  {availableClinics.map((clinic, index) => (
                    <button
                      key={clinic.employeeId}
                      onClick={() => setSelectedEmployeeId(clinic.employeeId)}
                      className={`w-full group relative overflow-hidden rounded-xl border-2 transition-all duration-300 text-left ${
                        selectedEmployeeId === clinic.employeeId
                          ? isDark
                            ? "border-[#174d7a] bg-gradient-to-br from-[#174d7a]/20 to-[#1d5c91]/10 shadow-lg shadow-[#174d7a]/20 scale-[1.02]"
                            : "border-[#174d7a] bg-gradient-to-br from-[#174d7a]/15 to-[#1d5c91]/5 shadow-lg shadow-[#174d7a]/20 scale-[1.02]"
                          : isDark
                          ? "border-slate-600 bg-slate-700/50 hover:border-[#174d7a]/50 hover:bg-slate-700/70 hover:shadow-md"
                          : "border-slate-300 bg-white hover:border-[#174d7a]/50 hover:bg-slate-50 hover:shadow-md"
                      }`}
                      style={{
                        animationDelay: `${index * 50}ms`,
                      }}
                    >
                      {/* Selection Indicator Bar */}
                      {selectedEmployeeId === clinic.employeeId && (
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#174d7a] to-[#1d5c91]"></div>
                      )}
                      
                      <div className="p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            {/* Clinic Icon */}
                            <div
                              className={`flex-shrink-0 p-2 rounded-lg transition-all duration-300 ${
                                selectedEmployeeId === clinic.employeeId
                                  ? "bg-[#174d7a] text-white shadow-md scale-105"
                                  : isDark
                                  ? "bg-slate-600/50 text-slate-300 group-hover:bg-slate-600"
                                  : "bg-gradient-to-br from-slate-100 to-slate-200 text-slate-700 group-hover:from-slate-200 group-hover:to-slate-300"
                              }`}
                            >
                              <BusinessIcon sx={{ fontSize: 22 }} />
                            </div>
                            
                            {/* Clinic Info */}
                            <div className="flex-1 min-w-0">
                              <h3
                                className={`font-semibold text-base mb-1.5 truncate ${
                                  isDark ? "text-white" : "text-slate-800"
                                }`}
                                title={clinic.clinicName}
                              >
                                {clinic.clinicName}
                              </h3>
                              
                              <div className="space-y-1.5">
                                {/* Role Badge */}
                                <div className="flex items-center gap-1.5">
                                  <PersonIcon
                                    sx={{
                                      fontSize: 14,
                                      color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
                                    }}
                                  />
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                                      selectedEmployeeId === clinic.employeeId
                                        ? "bg-[#174d7a]/20 text-[#174d7a]"
                                        : isDark
                                        ? "bg-slate-600/50 text-slate-300"
                                        : "bg-slate-100 text-slate-700"
                                    }`}
                                  >
                                    {clinic.roleName || "N/A"}
                                  </span>
                                </div>
                                
                                {/* Employee ID */}
                                <div className="flex items-center gap-1.5">
                                  <BadgeIcon
                                    sx={{
                                      fontSize: 12,
                                      color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                                    }}
                                  />
                                  <span
                                    className={`text-xs font-mono ${
                                      isDark ? "text-slate-400" : "text-slate-600"
                                    }`}
                                  >
                                    {clinic.employeeId || "N/A"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Selection Checkmark */}
                          <div className="flex-shrink-0">
                            {selectedEmployeeId === clinic.employeeId ? (
                              <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#174d7a] text-white shadow-md">
                                <CheckCircleIcon sx={{ fontSize: 18 }} />
                              </div>
                            ) : (
                              <div
                                className={`w-7 h-7 rounded-full border-2 transition-all duration-300 ${
                                  isDark
                                    ? "border-slate-500 group-hover:border-[#174d7a]/50"
                                    : "border-slate-300 group-hover:border-[#174d7a]/50"
                                }`}
                              ></div>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Proceed Button */}
                <button
                  onClick={handleClinicProceed}
                  disabled={!selectedEmployeeId || isLoading}
                  className={`w-full py-3 text-white font-semibold rounded-xl shadow-md transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDark
                      ? "bg-gradient-to-r from-[#174d7a] to-[#1d5c91] hover:from-[#1a5f95] hover:to-[#2169a3] shadow-[#174d7a]/30 hover:shadow-[#174d7a]/50"
                      : "bg-gradient-to-r from-[#174d7a] to-[#1d5c91] hover:from-[#1a5f95] hover:to-[#2169a3] shadow-[#174d7a]/30 hover:shadow-[#174d7a]/50"
                  }`}
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span className="text-sm">Processing...</span>
                    </>
                  ) : (
                    <>
                      <span className="text-sm">Proceed</span>
                      <ArrowForwardIcon sx={{ fontSize: 18 }} />
                    </>
                  )}
                </button>

                {/* Back Button */}
                <div className="mt-6 text-center">
                  <button
                    onClick={() => {
                      setShowClinicSelection(false);
                      setSelectedEmployeeId(null);
                      setAvailableClinics([]);
                    }}
                    className={`text-sm hover:underline ${
                      isDark ? "text-slate-400 hover:text-slate-300" : "text-slate-600 hover:text-slate-800"
                    }`}
                  >
                    Back to Login
                  </button>
                </div>
              </>
            ) : (
              // Login Form
              <>
                <style>{`
                  input:-webkit-autofill,
                  input:-webkit-autofill:hover,
                  input:-webkit-autofill:focus,
                  input:-webkit-autofill:active {
                    -webkit-text-fill-color: ${isDark ? "white" : "#1e293b"} !important;
                    -webkit-box-shadow: 0 0 0px 1000px ${
                      isDark ? "#1e293b" : "#ffffff"
                    } inset !important;
                    box-shadow: 0 0 0px 1000px ${
                      isDark ? "#1e293b" : "#ffffff"
                    } inset !important;
                    transition: background-color 5000s ease-in-out 0s !important;
                    caret-color: ${isDark ? "white" : "#1e293b"} !important;
                  }
                `}</style>
                <form onSubmit={onSubmit} className="space-y-5">
              {/* Email Field */}
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-slate-300" : "text-slate-700"
                  }`}
                >
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <EmailIcon
                      sx={{
                        fontSize: 20,
                        color: isDark
                          ? "rgba(255,255,255,0.4)"
                          : "rgba(0,0,0,0.4)",
                      }}
                    />
                  </div>
                  <input
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    onChange={handlechange}
                    value={formdata.email}
                    className={`w-full pl-12 pr-4 py-3.5 rounded-xl placeholder-opacity-40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all ${
                      isDark
                        ? "bg-slate-700/50 border border-slate-600 text-white placeholder-slate-400"
                        : "bg-slate-50 border border-slate-300 text-slate-800 placeholder-slate-500"
                    }`}
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-slate-300" : "text-slate-700"
                  }`}
                >
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <LockIcon
                      sx={{
                        fontSize: 20,
                        color: isDark
                          ? "rgba(255,255,255,0.4)"
                          : "rgba(0,0,0,0.4)",
                      }}
                    />
                  </div>
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    onChange={handlechange}
                    value={formdata.password}
                    className={`w-full pl-12 pr-12 py-3.5 rounded-xl placeholder-opacity-40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all ${
                      isDark
                        ? "bg-slate-700/50 border border-slate-600 text-white placeholder-slate-400"
                        : "bg-slate-50 border border-slate-300 text-slate-800 placeholder-slate-500"
                    }`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <VisibilityOffIcon
                        sx={{
                          fontSize: 20,
                          color: isDark
                            ? "rgba(255,255,255,0.4)"
                            : "rgba(0,0,0,0.4)",
                        }}
                      />
                    ) : (
                      <VisibilityIcon
                        sx={{
                          fontSize: 20,
                          color: isDark
                            ? "rgba(255,255,255,0.4)"
                            : "rgba(0,0,0,0.4)",
                        }}
                      />
                    )}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {formdata.error && (
                <div
                  className={`rounded-xl px-4 py-3 text-center border ${
                    isDark
                      ? "bg-red-500/20 border-red-500/30"
                      : "bg-red-50 border-red-200"
                  }`}
                >
                  <span
                    className={`text-sm font-medium ${
                      isDark ? "text-red-300" : "text-red-600"
                    }`}
                  >
                    Invalid credentials. Please try again.
                  </span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-4 text-white font-semibold rounded-xl shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDark
                    ? "bg-gradient-to-r from-[#174d7a] to-[#1d5c91] hover:from-[#1a5f95] hover:to-[#2169a3] shadow-[#174d7a]/30 hover:shadow-[#174d7a]/50"
                    : "bg-gradient-to-r from-[#174d7a] to-[#1d5c91] hover:from-[#1a5f95] hover:to-[#2169a3] shadow-[#174d7a]/30 hover:shadow-[#174d7a]/50"
                }`}
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <LoginIcon sx={{ fontSize: 20 }} />
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-8 text-center">
              <p
                className={`text-xs ${
                  isDark ? "text-slate-500" : "text-slate-400"
                }`}
              >
                © {new Date().getFullYear()} Care32 Dental Software. All rights
                reserved.
              </p>
            </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Drlogin;